import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavigationBar from "../components/Navbar";
import Footer from "../components/Footer";
import PageSEO from "../components/PageSEO";
import ProductOfTheMonth from "../components/ProductOfTheMonth";
import CategoryCollections from "../components/CategoryCollections";
import NewCustomerDiscount from "../components/NewCustomerDiscount";
import ProductRecommendations from "../components/ProductRecommendations";
import NewsletterSignup from "../components/NewsletterSignup";
import { Container, Row, Col, Button } from "react-bootstrap";
import {
  FaTruck,
  FaTools,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaBox,
  FaHeadset,
  FaGlobe,
} from "react-icons/fa";
import "./HomePageModern.css";
import { getImageUrl, API_URL, getPlaceholderImage } from '../config';
// Hero image is served from uploads directory

function HomePageModern() {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productOfTheMonth, setProductOfTheMonth] = useState(null);
  const [monthlyVariantId, setMonthlyVariantId] = useState(null);
  const [monthlyDiscount, setMonthlyDiscount] = useState(20);
  const [loading, setLoading] = useState(true);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "TR Ventilation",
    "description": "Professional HVAC & Ventilation Supplies",
    "url": "https://trventilation.com",
    "telephone": "+441482205358",
    "email": "sales@trventilation.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Technology Park, 5 Hamburg Rd",
      "addressLocality": "Kingston upon Hull",
      "addressRegion": "East Yorkshire",
      "postalCode": "HU7 0WD",
      "addressCountry": "GB"
    },
    "areaServed": ["UK", "Yorkshire", "Hull"],
    "priceRange": "£",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured products
        const productsResponse = await fetch(`${API_URL}/products?status=active&limit=6`);
        if (productsResponse.ok) {
          const data = await productsResponse.json();
          setFeaturedProducts(data.products || []);
        }

        // Fetch product of the month settings
        const settingsResponse = await fetch(`${API_URL}/site-settings`);
        if (settingsResponse.ok) {
          const settings = await settingsResponse.json();
          if (settings.productOfTheMonth?.productId) {
            // Fetch the full product details
            const productResponse = await fetch(
              `${API_URL}/products/${settings.productOfTheMonth.productId}`
            );
            if (productResponse.ok) {
              const productData = await productResponse.json();
              setProductOfTheMonth(productData);
              setMonthlyVariantId(settings.productOfTheMonth.variantId || null);
              setMonthlyDiscount(settings.productOfTheMonth.discountPercentage || 20);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const features = [
    {
      icon: FaTruck,
      title: "Fast Delivery",
      description: "1-2 day delivery across the UK on in-stock items",
    },
    {
      icon: FaShieldAlt,
      title: "Quality Guaranteed",
      description: "All products backed by comprehensive manufacturer warranties",
    },
    {
      icon: FaTools,
      title: "Expert Support",
      description: "Technical assistance from certified HVAC professionals",
    },
    {
      icon: FaHeadset,
      title: "Dedicated Support",
      description: "Call us for technical advice and custom quotes",
    },
  ];

  const stats = [
    { number: "15+", label: "Years Experience" },
    { number: "300+", label: "Products Available" },
    { number: "5000+", label: "Happy Customers" },
    { number: "48hrs", label: "UK Delivery" },
  ];

  return (
    <>
      <PageSEO
        title="Commercial HVAC & Ductwork Supplies Hull | Industrial Ventilation UK"
        description="Premium commercial HVAC supplies and industrial ventilation equipment. Buy ductwork, fans, extraction systems online with next-day UK delivery."
        keywords="commercial HVAC supplies, ductwork supplies Yorkshire, industrial ventilation systems"
        structured={organizationSchema}
      />
      <NavigationBar />

      {/* ============================================
          MODERN HERO SECTION
          ============================================ */}
      <section className="modern-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="hero-content mb-4 mb-lg-0">
              <h1 className="hero-title-modern">
                Premium HVAC & Ventilation Solutions
              </h1>
              <p className="hero-subtitle-modern">
                Your trusted partner for industrial ventilation supplies in Hull and Yorkshire. Quality products, competitive prices, and exceptional service you can rely on.
              </p>

              <div className="hero-cta-group">
                <Button
                  className="btn-primary-modern"
                  size="lg"
                  onClick={() => navigate("/products")}
                >
                  Browse Products
                  <FaArrowRight className="ms-2" size={14} />
                </Button>
                <Button
                  className="btn-secondary-modern"
                  size="lg"
                  onClick={() => navigate("/contact")}
                >
                  Get a Quote
                </Button>
              </div>

              <div className="trust-pills">
                <div className="trust-pill">
                  <FaCheckCircle />
                  Trade Accounts Save 15%+
                </div>
                <div className="trust-pill">
                  <FaCheckCircle />
                  Next-Day UK Delivery
                </div>
              </div>
            </Col>

            <Col lg={6} className="hero-image-modern">
              <img
                src={getImageUrl('/uploads/products/home-hero.jpg')}
                alt="Professional HVAC ventilation ductwork installation"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============================================
          STATS SECTION
          ============================================ */}
      <section className="stats-section">
        <Container>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ============================================
          MODERN FEATURES SECTION
          ============================================ */}
      <section className="features-section-modern">
        <Container>
          <div className="section-header-modern">
            <div className="section-divider-modern"></div>
            <h2 className="section-title-modern">Why Choose Us</h2>
            <p className="section-subtitle-modern">
              We deliver excellence across every aspect of our service
            </p>
          </div>

          <div className="features-grid-modern">
            {features.map((feature, index) => (
              <div key={index} className="feature-card-modern">
                <feature.icon className="feature-icon-modern" />
                <h3 className="feature-title-modern">{feature.title}</h3>
                <p className="feature-text-modern">{feature.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ============================================
          NEW CUSTOMER DISCOUNT SECTION
          ============================================ */}
      <section className="new-customer-section-modern">
        <Container>
          <NewCustomerDiscount />
        </Container>
      </section>

      {/* ============================================
          PRODUCT OF THE MONTH SECTION
          ============================================ */}
      {productOfTheMonth && (
        <section className="product-of-month-section-modern">
          <Container>
            <ProductOfTheMonth product={productOfTheMonth} discount={monthlyDiscount} variantId={monthlyVariantId} />
          </Container>
        </section>
      )}

      {/* ============================================
          CATEGORY COLLECTIONS SECTION
          ============================================ */}
      <CategoryCollections />

      {/* ============================================
          PRODUCTS SECTION
          ============================================ */}
      <section className="products-section-modern">
        <Container>
          <div className="section-header-modern">
            <div className="section-divider-modern"></div>
            <h2 className="section-title-modern">Featured Products</h2>
            <p className="section-subtitle-modern">
              Explore our range of premium ventilation and ducting solutions
            </p>
          </div>

          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Row>
              {featuredProducts.slice(0, 6).map((product) => (
                <Col lg={4} md={6} key={product._id} className="mb-4">
                  <div className="product-card-modern">
                    <div className="product-img-wrapper-modern">
                      <img
                        src={getImageUrl(product.image || product.images?.[0])}
                        alt={product.name}
                        onError={(e) => {
                          e.target.src = getPlaceholderImage('No Image');
                        }}
                      />
                      {product.inStock && (
                        <span className="product-badge-modern">In Stock</span>
                      )}
                    </div>

                    <div className="product-info-modern">
                      <h4 className="product-name-modern">{product.name}</h4>
                      <p className="product-desc-modern">
                        {product.shortDescription || product.description?.substring(0, 80)}...
                      </p>

                      <div className="product-footer-modern">
                        <span className="product-price-modern">
                          £{Number(product.price).toFixed(2)}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/product/${product._id}`)}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          <div className="text-center mt-5">
            <Button
              size="lg"
              className="btn-primary-modern"
              onClick={() => navigate("/products")}
            >
              View All Products
              <FaArrowRight className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* ============================================
          TRENDING PRODUCTS SECTION
          ============================================ */}
      <section className="trending-section-modern">
        <Container>
          <ProductRecommendations type="trending" />
        </Container>
      </section>

      {/* ============================================
          WHY CHOOSE US SECTION
          ============================================ */}
      <section className="why-choose-us-section-modern">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="section-header-modern">
                <div className="section-divider-modern"></div>
                <h2 className="section-title-modern">Why Choose TR Ventilation?</h2>
                <p className="section-subtitle-modern">
                  15+ years of industry expertise delivering premium HVAC solutions
                </p>
              </div>

              <div className="benefits-list-modern">
                <div className="benefit-item-modern">
                  <FaCheckCircle className="benefit-icon-modern" />
                  <div>
                    <h4>Extensive Inventory</h4>
                    <p>Full range of ducting, fans, and accessories in stock</p>
                  </div>
                </div>

                <div className="benefit-item-modern">
                  <FaCheckCircle className="benefit-icon-modern" />
                  <div>
                    <h4>Competitive Pricing</h4>
                    <p>Trade discounts and bulk order rates available</p>
                  </div>
                </div>

                <div className="benefit-item-modern">
                  <FaCheckCircle className="benefit-icon-modern" />
                  <div>
                    <h4>Expert Support</h4>
                    <p>Certified HVAC professionals ready to help</p>
                  </div>
                </div>

                <div className="benefit-item-modern">
                  <FaCheckCircle className="benefit-icon-modern" />
                  <div>
                    <h4>Fast Delivery</h4>
                    <p>Next-day UK delivery on in-stock items</p>
                  </div>
                </div>

                <div className="benefit-item-modern">
                  <FaCheckCircle className="benefit-icon-modern" />
                  <div>
                    <h4>Quality Guaranteed</h4>
                    <p>All products backed by manufacturer warranties</p>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="why-choose-us-content-modern">
                <div className="info-box-modern">
                  <h3>Trusted by HVAC Professionals</h3>
                  <p>
                    Since 2023, TR Ventilation has been the preferred supplier for contractors, builders, and
                    facility managers across Hull and Yorkshire. We understand the critical importance of quality
                    ventilation systems and professional support in commercial and industrial environments.
                  </p>
                  <p style={{ marginBottom: '1.5rem' }}>
                    As specialists in commercial HVAC supplies, ductwork systems, and industrial ventilation,
                    we provide comprehensive solutions tailored to your needs.
                  </p>
                  <Button
                    className="btn-primary-modern"
                    size="lg"
                    onClick={() => navigate("/about")}
                  >
                    Learn More About Us
                    <FaArrowRight className="ms-2" />
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ============================================
          CTA SECTION
          ============================================ */}
      <section className="cta-section-modern">
        <Container>
          <div className="cta-content">
            <h2 className="cta-title-modern">
              Ready to Find Your Perfect Solution?
            </h2>
            <p className="cta-subtitle-modern">
              Browse our complete range of ventilation products or contact our team for a custom quote tailored to your needs.
            </p>

            <div className="hero-cta-group justify-content-center">
              <Button
                className="btn-primary-modern"
                size="lg"
                onClick={() => navigate("/products")}
              >
                Browse Catalog
              </Button>
              <Button
                className="btn-secondary-modern"
                size="lg"
                onClick={() => navigate("/contact")}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </Container>
      </section>


      <Footer />
    </>
  );
}

export default HomePageModern;
